import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React from 'react';
import { paymentApi, type Plan } from '@/api/payment';
import { Warning2, ArrowRight, Tick, ExportSquare, Spinner } from '@/components/icons';
import { markSubscriptionCheckoutStarted } from '@/lib/analytics';
import { capture } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import type { PlanKey } from '@/lib/analytics-events';

const PLAN_ORDER = ['BASIC', 'INTERMEDIATE', 'PRO'];

const PLAN_ALIASES: Record<string, string> = {
    basic: 'BASIC',
    'basic plan': 'BASIC',
    'plan-basic': 'BASIC',
    básico: 'BASIC',
    intermediate: 'INTERMEDIATE',
    'intermediate plan': 'INTERMEDIATE',
    'plan-professional': 'INTERMEDIATE',
    professional: 'INTERMEDIATE',
    profissional: 'INTERMEDIATE',
    enterprise: 'PRO',
    'plan-enterprise': 'PRO',
    pro: 'PRO',
    'pro plan': 'PRO',
};

const PLAN_PRESENTATION: Record<
    string,
    {
        badge?: string;
        highlight: boolean;
        tagline: string;
    }
> = {
    BASIC: {
        highlight: false,
        tagline: 'Ideal para quem quer começar com uma estrutura mais organizada.',
    },
    INTERMEDIATE: {
        badge: 'Mais escolhido',
        highlight: true,
        tagline: 'A opção mais equilibrada para uma rotina clínica mais intensa.',
    },
    PRO: {
        highlight: false,
        tagline: 'Para operações maiores, com mais volume, suporte e previsibilidade.',
    },
};

type PlansListProps = {
    activePlanName?: string | null;
    compact?: boolean;
    fullWidth?: boolean;
    nextPaymentDate?: string | number | null;
    onManageSubscription?: () => void;
    isManageLoading?: boolean;
};

const normalizePlanKey = (value?: string | null) => {
    if (!value) return '';

    const normalized = value.trim().toLowerCase();
    return PLAN_ALIASES[normalized] || value.trim().toUpperCase();
};

const getPlanKey = (plan: Plan) => {
    const idKey = normalizePlanKey(plan.id);
    if (PLAN_PRESENTATION[idKey]) return idKey;

    const nameKey = normalizePlanKey(plan.name);
    if (PLAN_PRESENTATION[nameKey]) return nameKey;

    return idKey || nameKey;
};

const sortPlans = (plans: Plan[]) => {
    return [...plans].sort((first, second) => {
        const firstIndex = PLAN_ORDER.indexOf(getPlanKey(first));
        const secondIndex = PLAN_ORDER.indexOf(getPlanKey(second));

        if (firstIndex === -1 && secondIndex === -1) {
            return first.name.localeCompare(second.name, 'pt-BR');
        }

        if (firstIndex === -1) return 1;
        if (secondIndex === -1) return -1;

        return firstIndex - secondIndex;
    });
};

const formatNextPaymentDate = (value?: string | number | null) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return format(date, 'dd/MM/yyyy', {
        locale: ptBR,
    });
};

const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'decimal',
    }).format(price / 100);
};

const getCurrencyLabel = (currency: string) => {
    return currency.toUpperCase() === 'BRL' ? 'R$' : currency.toUpperCase();
};

const isSamePlan = (plan: Plan, activePlanName?: string | null) => {
    if (!activePlanName) return false;

    const activePlan = activePlanName.trim().toLowerCase();
    const activePlanKey = normalizePlanKey(activePlanName);

    return (
        getPlanKey(plan) === activePlanKey ||
        plan.id.trim().toLowerCase() === activePlan ||
        plan.name.trim().toLowerCase() === activePlan
    );
};

const PriceDisplay: React.FC<{
    compact?: boolean;
    currency: string;
    onDark: boolean;
    period: string;
    price: number;
}> = ({ compact = false, currency, onDark, period, price }) => {
    if (price === 0) {
        return (
            <div className="mb-1 mt-[1.125rem] flex items-end gap-2">
                <span
                    className={cn(
                        'font-sans font-semibold leading-none tracking-[-0.03em]',
                        compact
                            ? 'text-[clamp(2.125rem,4vw,3rem)]'
                            : 'text-[clamp(2.75rem,5vw,4.25rem)]',
                        onDark ? 'text-brand-foreground' : 'text-brand',
                    )}
                >
                    Gratuito
                </span>
                <span
                    className={cn(
                        'mb-1 text-sm font-medium',
                        onDark ? 'text-brand-foreground/60' : 'text-muted-foreground',
                    )}
                >
                    {period}
                </span>
            </div>
        );
    }

    return (
        <div className="mb-1 mt-[1.125rem] flex items-baseline gap-1.5">
            <span
                className={cn(
                    'font-medium leading-none',
                    compact ? 'text-sm' : 'text-lg',
                    onDark ? 'text-brand-foreground/70' : 'text-muted-foreground',
                )}
            >
                {getCurrencyLabel(currency)}
            </span>
            <span
                className={cn(
                    'font-sans font-semibold leading-none tracking-[-0.03em]',
                    compact
                        ? 'text-[clamp(2.5rem,4.5vw,3.25rem)]'
                        : 'text-[clamp(3.25rem,5vw,4.75rem)]',
                    onDark ? 'text-brand-foreground' : 'text-brand',
                )}
            >
                {formatPrice(price)}
            </span>
            <span
                className={cn(
                    'text-sm font-medium leading-none',
                    onDark ? 'text-brand-foreground/60' : 'text-muted-foreground',
                )}
            >
                {period}
            </span>
        </div>
    );
};

const FeatureRow: React.FC<{
    children: React.ReactNode;
    onDark: boolean;
}> = ({ children, onDark }) => {
    return (
        <li
            className={cn(
                'flex items-start gap-3 border-b border-dashed py-2.5 text-sm leading-6 md:text-[0.95rem]',
                onDark
                    ? 'border-brand-foreground/10 text-brand-foreground/90'
                    : 'border-border text-foreground',
            )}
        >
            <span className="mt-1 grid size-4 shrink-0 place-items-center rounded-full bg-info">
                <Tick className="size-2.5 text-info-foreground" />
            </span>
            <span>{children}</span>
        </li>
    );
};

const PricingSkeleton: React.FC<{ compact?: boolean; featured?: boolean }> = ({
    compact = false,
    featured = false,
}) => {
    return (
        <div
            className={cn(
                'flex h-full flex-col overflow-hidden rounded-4xl border',
                compact ? 'min-h-[30rem] p-5' : 'min-h-[35rem] p-8 lg:p-9',
                featured
                    ? 'border-brand-foreground/20 bg-brand'
                    : 'border-border bg-card shadow-elevation-2',
            )}
        >
            <div
                className={cn(
                    'h-3 w-28 animate-pulse rounded-full',
                    featured ? 'bg-brand-foreground/10' : 'bg-brand-subtle',
                )}
            />
            <div
                className={cn(
                    'mt-5 h-10 w-40 animate-pulse rounded-xl',
                    featured ? 'bg-brand-foreground/10' : 'bg-muted',
                )}
            />
            <div
                className={cn(
                    'mt-4 h-6 w-36 animate-pulse rounded-full',
                    featured ? 'bg-brand-foreground/10' : 'bg-muted',
                )}
            />
            <div
                className={cn(
                    'mt-8 h-12 w-full animate-pulse rounded-2xl',
                    featured ? 'bg-brand-foreground/10' : 'bg-muted',
                )}
            />
            <div className="mt-8 flex-1 space-y-3">
                {[1, 2, 3, 4, 5].map((item) => (
                    <div
                        key={item}
                        className={cn(
                            'h-4 animate-pulse rounded-full',
                            featured ? 'bg-brand-foreground/10' : 'bg-muted',
                        )}
                    />
                ))}
            </div>
        </div>
    );
};

export const PlansList: React.FC<PlansListProps> = ({
    activePlanName,
    compact = false,
    fullWidth = false,
    nextPaymentDate,
    onManageSubscription,
    isManageLoading = false,
}) => {
    const [isCheckingOut, setIsCheckingOut] = React.useState<string | null>(null);
    const {
        data: plans,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['plans'],
        queryFn: paymentApi.getPlans,
    });

    const sortedPlans = React.useMemo(() => sortPlans(plans || []), [plans]);

    const handlePlanSelect = async (plan: Plan) => {
        const planId = plan.id;

        try {
            setIsCheckingOut(planId);
            markSubscriptionCheckoutStarted({
                hadSubscription: Boolean(activePlanName),
            });
            capture('plan_checkout_started', {
                plan: planId as PlanKey,
                had_subscription: Boolean(activePlanName),
            });
            const response = await paymentApi.checkout({ planId });
            window.location.href = response.url;
        } catch (error) {
            console.error('Checkout error:', error);
            setIsCheckingOut(null);
        }
    };

    const nextPaymentLabel = formatNextPaymentDate(nextPaymentDate);

    return (
        <section
            className={cn(
                'relative overflow-hidden rounded-4xl',
                compact ? 'py-2' : fullWidth ? 'py-8' : 'px-4 py-8 sm:px-6 lg:px-8',
            )}
        >
            <div className={cn('relative', fullWidth ? 'w-full' : 'mx-auto max-w-[1100px]')}>
                {isLoading ? (
                    <div
                        className={cn('grid items-stretch', compact ? 'gap-3' : 'gap-5')}
                        style={{
                            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${compact ? '16rem' : '20rem'}), 1fr))`,
                        }}
                    >
                        <PricingSkeleton compact={compact} />
                        <PricingSkeleton compact={compact} featured />
                        <PricingSkeleton compact={compact} />
                    </div>
                ) : isError ? (
                    <div className="flex flex-col items-center justify-center gap-3 rounded-4xl border border-border bg-card px-6 py-10 text-center text-foreground shadow-elevation-2">
                        <Warning2 className="size-8 text-destructive" />
                        <p>Erro ao carregar planos. Tente novamente mais tarde.</p>
                    </div>
                ) : sortedPlans.length === 0 ? (
                    <div className="rounded-4xl border border-border bg-card px-6 py-10 text-center text-foreground shadow-elevation-2">
                        Não foi possível carregar os planos agora.
                    </div>
                ) : (
                    <>
                        <div
                            className={cn('grid items-stretch', compact ? 'gap-3' : 'gap-5')}
                            style={{
                                gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${compact ? '16rem' : '20rem'}), 1fr))`,
                            }}
                        >
                            {sortedPlans.map((plan) => {
                                const planKey = getPlanKey(plan);
                                const presentation = PLAN_PRESENTATION[planKey] || {
                                    highlight: false,
                                    tagline:
                                        'Plano de assinatura para organizar melhor sua rotina clínica.',
                                };
                                const onDark = presentation.highlight;
                                const isActive = isSamePlan(plan, activePlanName);
                                const checkoutPriceId = plan.price_id;
                                const planIdentifier = plan.id;

                                return (
                                    <div key={planIdentifier} className="relative flex h-full pt-4">
                                        {presentation.badge ? (
                                            <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2">
                                                <span className="rounded-full bg-info px-3.5 py-1.5 text-[0.62rem] font-bold uppercase tracking-[0.2em] text-info-foreground">
                                                    {presentation.badge}
                                                </span>
                                            </div>
                                        ) : null}

                                        <article
                                            className={cn(
                                                'relative flex h-full flex-1 flex-col overflow-hidden rounded-4xl border transition-transform duration-300',
                                                compact
                                                    ? 'min-h-[30rem] p-5'
                                                    : 'min-h-[35rem] p-8 lg:p-9',
                                                onDark
                                                    ? '-translate-y-2 border-brand-foreground/20 bg-brand'
                                                    : 'border-border bg-card shadow-elevation-2',
                                            )}
                                        >
                                            <div className="relative z-10 flex min-h-8 flex-wrap items-center gap-2">
                                                {isActive ? (
                                                    <span
                                                        className={cn(
                                                            'rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.14em]',
                                                            onDark
                                                                ? 'bg-brand-foreground/10 text-brand-foreground'
                                                                : 'bg-brand-subtle text-brand',
                                                        )}
                                                    >
                                                        Plano atual
                                                    </span>
                                                ) : null}
                                            </div>

                                            <h3
                                                className={cn(
                                                    'relative z-10 mt-1 text-[clamp(1.75rem,2.6vw,2.25rem)] font-semibold leading-[1.05] tracking-[-0.02em]',
                                                    onDark ? 'text-brand-foreground' : 'text-brand',
                                                )}
                                            >
                                                {plan.name}
                                            </h3>

                                            <p
                                                className={cn(
                                                    'relative z-10 mt-3 min-h-[3rem] text-sm leading-6 md:text-base',
                                                    onDark
                                                        ? 'text-brand-foreground/70'
                                                        : 'text-muted-foreground',
                                                )}
                                            >
                                                {presentation.tagline}
                                            </p>

                                            {isActive ? (
                                                <p
                                                    className={cn(
                                                        'relative z-10 mt-3 text-sm font-medium',
                                                        onDark
                                                            ? 'text-brand-foreground/60'
                                                            : 'text-muted-foreground',
                                                    )}
                                                >
                                                    Próximo pagamento em {nextPaymentLabel}
                                                </p>
                                            ) : null}

                                            <PriceDisplay
                                                compact={compact}
                                                currency={plan.currency}
                                                onDark={onDark}
                                                period="/mês"
                                                price={plan.price}
                                            />

                                            <ul className="relative z-10 mt-6 flex-1 list-none p-0">
                                                {plan.features.map((feature) => (
                                                    <FeatureRow
                                                        key={`${planIdentifier}-${feature}`}
                                                        onDark={onDark}
                                                    >
                                                        {feature}
                                                    </FeatureRow>
                                                ))}
                                            </ul>

                                            <div className="relative z-10 mt-6 space-y-3">
                                                {isActive ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={onManageSubscription}
                                                            disabled={
                                                                isManageLoading ||
                                                                !onManageSubscription
                                                            }
                                                            className={cn(
                                                                'inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-60',
                                                                onDark
                                                                    ? 'bg-brand-foreground text-brand'
                                                                    : 'bg-brand text-brand-foreground shadow-elevation-2',
                                                            )}
                                                        >
                                                            {isManageLoading ? (
                                                                <>
                                                                    <Spinner className="size-4 animate-spin" />
                                                                    Redirecionando...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    Gerenciar pagamento
                                                                    <ExportSquare className="size-4" />
                                                                </>
                                                            )}
                                                        </button>
                                                        <p
                                                            className={cn(
                                                                'text-center text-xs leading-5',
                                                                onDark
                                                                    ? 'text-brand-foreground/60'
                                                                    : 'text-muted-foreground',
                                                            )}
                                                        >
                                                            Você será direcionado para o portal de
                                                            pagamentos da Stripe.
                                                        </p>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (checkoutPriceId) {
                                                                void handlePlanSelect(plan);
                                                            }
                                                        }}
                                                        disabled={
                                                            !!isCheckingOut || !checkoutPriceId
                                                        }
                                                        className={cn(
                                                            'inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-60',
                                                            onDark
                                                                ? 'bg-brand-foreground text-brand'
                                                                : 'bg-brand text-brand-foreground shadow-elevation-2',
                                                        )}
                                                    >
                                                        {isCheckingOut === plan.id ? (
                                                            <>
                                                                <Spinner className="size-4 animate-spin" />
                                                                Processando...
                                                            </>
                                                        ) : !checkoutPriceId ? (
                                                            'Plano indisponível'
                                                        ) : (
                                                            <>
                                                                Escolher {plan.name}
                                                                <ArrowRight className="size-4" />
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </article>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};
